import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Copy, Check, ExternalLink, Code2 } from 'lucide-react';

export default function WidgetEmbedCode({ agencyId }) {
    const [style, setStyle] = useState('card');
    const [theme, setTheme] = useState('light');
    const [copied, setCopied] = useState(false);

    const baseUrl = window.location.origin;
    const widgetUrl = `${baseUrl}/api/widget/${agencyId}?style=${style}&theme=${theme}`;

    const iframeCode = style === 'card'
        ? `<iframe src="${widgetUrl}" width="340" height="220" frameborder="0" style="border:none;overflow:hidden;border-radius:16px;" title="Homly Badge"></iframe>`
        : `<iframe src="${widgetUrl}" width="500" height="60" frameborder="0" style="border:none;overflow:hidden;border-radius:12px;" title="Homly Badge"></iframe>`;

    const handleCopy = () => {
        navigator.clipboard.writeText(iframeCode);
        setCopied(true);
        toast.success('Code copié !');
        setTimeout(() => setCopied(false), 2000);
    };

    if (!agencyId) return null;

    return (
        <div className="bg-card rounded-2xl border border-border overflow-hidden" data-testid="widget-embed-section">
            <div className="p-6 border-b border-border">
                <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                    <Code2 className="w-5 h-5 text-[#681793]" />
                    Widget Badge Homly
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                    Ajoutez ce badge sur le site web de votre agence pour afficher vos avis
                </p>
            </div>

            <div className="p-6 space-y-5">
                {/* Style selector */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Format</label>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setStyle('card')}
                            className={`flex-1 p-3 rounded-xl border text-sm font-medium transition-all ${
                                style === 'card'
                                    ? 'border-[#681793] bg-[#681793]/10 text-[#681793]'
                                    : 'border-border text-muted-foreground hover:border-border/80'
                            }`}
                            data-testid="widget-style-card"
                        >
                            Card
                            <span className="block text-[10px] font-normal mt-0.5 opacity-70">Encart complet avec détails</span>
                        </button>
                        <button
                            onClick={() => setStyle('compact')}
                            className={`flex-1 p-3 rounded-xl border text-sm font-medium transition-all ${
                                style === 'compact'
                                    ? 'border-[#681793] bg-[#681793]/10 text-[#681793]'
                                    : 'border-border text-muted-foreground hover:border-border/80'
                            }`}
                            data-testid="widget-style-compact"
                        >
                            Compact
                            <span className="block text-[10px] font-normal mt-0.5 opacity-70">Barre horizontale discrète</span>
                        </button>
                    </div>
                </div>

                {/* Theme selector */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Thème</label>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setTheme('light')}
                            className={`flex-1 p-2.5 rounded-xl border text-xs font-medium transition-all flex items-center justify-center gap-2 ${
                                theme === 'light'
                                    ? 'border-[#681793] bg-[#681793]/10 text-[#681793]'
                                    : 'border-border text-muted-foreground'
                            }`}
                            data-testid="widget-theme-light"
                        >
                            <span className="w-4 h-4 rounded-full bg-white border border-gray-200"></span>
                            Clair
                        </button>
                        <button
                            onClick={() => setTheme('dark')}
                            className={`flex-1 p-2.5 rounded-xl border text-xs font-medium transition-all flex items-center justify-center gap-2 ${
                                theme === 'dark'
                                    ? 'border-[#681793] bg-[#681793]/10 text-[#681793]'
                                    : 'border-border text-muted-foreground'
                            }`}
                            data-testid="widget-theme-dark"
                        >
                            <span className="w-4 h-4 rounded-full bg-[#1a1a2e] border border-gray-600"></span>
                            Sombre
                        </button>
                    </div>
                </div>

                {/* Preview */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Aperçu</label>
                    <div className={`rounded-xl p-6 flex items-center justify-center min-h-[120px] ${theme === 'light' ? 'bg-gray-100' : 'bg-[#0d0d1a]'}`}>
                        {/* Static preview mockup */}
                        {style === 'card' ? (
                            <div className={`max-w-[320px] w-full rounded-2xl p-5 border ${theme === 'light' ? 'bg-white border-gray-200 text-gray-900' : 'bg-[#1a1a2e] border-white/10 text-white'}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm font-bold">Homly<span className="text-[#FF914D]">.</span></span>
                                    <span className="text-[8px] px-2 py-0.5 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold">Avis 100% vérifiés</span>
                                </div>
                                <div className="flex gap-4 mb-3">
                                    <div>
                                        <p className={`text-[9px] ${theme === 'light' ? 'text-gray-500' : 'text-white/50'}`}>Avis Homly</p>
                                        <div className="flex items-center gap-1"><span className="text-lg font-bold">4.8</span><span className="text-yellow-400 text-xs">★★★★★</span></div>
                                    </div>
                                    <div>
                                        <p className={`text-[9px] ${theme === 'light' ? 'text-gray-500' : 'text-white/50'}`}>Avis Google</p>
                                        <div className="flex items-center gap-1"><span className="text-lg font-bold">4.7</span><span className="text-yellow-400 text-xs">★★★★★</span></div>
                                    </div>
                                </div>
                                <div className={`flex items-center justify-between rounded-lg p-2 ${theme === 'light' ? 'bg-gray-50' : 'bg-white/5'}`}>
                                    <span className={`text-[9px] ${theme === 'light' ? 'text-gray-500' : 'text-white/50'}`}>Ligue Or</span>
                                    <span className="text-xs font-bold text-[#681793]">1 250 HomlyScore</span>
                                </div>
                            </div>
                        ) : (
                            <div className={`max-w-[480px] w-full flex items-center gap-3 rounded-xl px-4 py-2.5 border ${theme === 'light' ? 'bg-white border-gray-200 text-gray-900' : 'bg-[#1a1a2e] border-white/10 text-white'}`}>
                                <span className="text-sm font-bold">Homly<span className="text-[#FF914D]">.</span></span>
                                <div className={`w-px h-5 ${theme === 'light' ? 'bg-gray-200' : 'bg-white/10'}`} />
                                <span className="text-xs font-bold">4.8</span>
                                <span className={`text-[9px] ${theme === 'light' ? 'text-gray-500' : 'text-white/50'}`}>12 avis</span>
                                <div className={`w-px h-5 ${theme === 'light' ? 'bg-gray-200' : 'bg-white/10'}`} />
                                <span className="text-xs font-bold">4.7</span>
                                <span className={`text-[9px] ${theme === 'light' ? 'text-gray-500' : 'text-white/50'}`}>119 avis</span>
                                <span className="text-[7px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold ml-auto">Certifié</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Code */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Code à copier</label>
                    <div className="relative">
                        <pre className="p-3 rounded-xl bg-white/5 border border-border text-[10px] text-muted-foreground overflow-x-auto whitespace-pre-wrap break-all font-mono">
                            {iframeCode}
                        </pre>
                        <Button
                            size="sm"
                            onClick={handleCopy}
                            className="absolute top-2 right-2 h-7 text-xs"
                            data-testid="widget-copy-btn"
                        >
                            {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                            {copied ? 'Copié' : 'Copier'}
                        </Button>
                    </div>
                </div>

                {/* Preview link */}
                <a
                    href={widgetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-[#681793] hover:text-[#681793]/80"
                    data-testid="widget-preview-link"
                >
                    <ExternalLink className="w-3 h-3" />
                    Voir le widget en pleine page
                </a>
            </div>
        </div>
    );
}
